<template>
  <div>
    <v-layout column @click="onClick" class="pa-2 cabinet-row" style="cursor: pointer;">
      <v-layout>
        <v-flex v-for="(header, index) in headers" :key="index" xs12 :class="flex_settings[header][lang]">
          <span class="body-1 font-weight-bold" style="color: #7E57C2">{{ $t(header) }}</span>
        </v-flex>
      </v-layout>
      <v-layout>
        <v-flex xs12 :class="flex_settings['Cabinet ID'][lang]" class="py-2">
          <v-layout>
            <!-- always resolved through getCabinetStatusIcon so retrofit /
                 pending verification also override the virtual cabinet icon -->
            <img :src="getCabinetStatusIcon(cabinet)" contain :height="icon_size" :width="icon_size"/>
            <v-layout column v-if="cabinet.description">
              <h3 :class="lang === 'en' ? 'ml-3' : 'mr-3'" style="word-break: break-word;">{{ cabinet.cabinet_id }}</h3>
              <h3 :class="lang === 'en' ? 'ml-3' : 'mr-3'" style="word-break: break-word;">{{ cabinet.description }}</h3>
            </v-layout>
            <h3 v-else :class="lang === 'en' ? 'ml-3' : 'mr-3'" style="word-break: break-word;">{{ cabinet.cabinet_id }}</h3>
          </v-layout>
        </v-flex>
        <v-flex xs12 :class="flex_settings['Controlled Fixtures'][lang]">
          <v-layout column class="py-2">
            <div v-if="!fixtures_summary.loaded">
              <v-progress-circular indeterminate="indeterminate" color="primary"></v-progress-circular>
            </div>
            <template v-else>
              <v-layout row>
                <span class="body-1">{{ $t('Total') }}:</span>
                <span class="font-weight-bold body-1 mx-1">{{ fixtures_summary.total }}</span>
              </v-layout>
              <v-layout row align-center>
                <span class="body-1">{{ $t('Connected And Active') }}:</span>
                <span
                  class="body-2 mx-1 font-weight-bold overflow-style"
                  :class="fixtures_summary.percentage < 60 ? 'red--text' : fixtures_summary.percentage > 89 ? 'green--text' : 'orange--text'"
                >
                  {{ fixtures_summary.percentage }}%
                </span>
                <v-tooltip v-if="fixtures_summary.retrofit_count" top style="z-index: 5">
                  <v-icon slot="activator" size="16" color="#8a90b3">mdi-information-outline</v-icon>
                  <span>{{ retrofitTooltipText }}</span>
                </v-tooltip>
              </v-layout>
            </template>
          </v-layout>
        </v-flex>
        <v-flex xs12 :class="flex_settings['Services'][lang]">
          <v-layout row class="py-2" v-if="hydrated">
            <v-tooltip top style="z-index: 5">
              <v-img slot="activator" contain :src="bellIcon(cabinet.analytics_alerts_status[0])" alt="" width="20" height="20" />
              <span>{{ cabinet.analytics_alerts_status[1] }}</span>
            </v-tooltip>
            <v-tooltip top style="z-index: 5" class="mx-2">
              <v-img slot="activator" contain :src="calendarIcon(cabinet.devices_schedule_status[0])" alt="" width="20" height="20" />
              <span>{{ cabinet.devices_schedule_status[1] }}</span>
            </v-tooltip>
            <v-tooltip top style="z-index: 5">
              <v-img slot="activator" contain :src="streetLightIcon(cabinet.power_validation_status[0])" alt="" width="20" height="20" />
              <span>{{ cabinet.power_validation_status[1] }}</span>
            </v-tooltip>
            <v-tooltip v-if="cabinet.class_name !== 'virtual_cabinet'" top style="z-index: 5" class="mx-2">
              <v-img slot="activator" contain :src="cabinetAlertsIcon(cabinet.cabinet_alerts_status && cabinet.cabinet_alerts_status[0])" alt="" width="20" height="20" />
              <span>{{ cabinet.cabinet_alerts_status[1] }}</span>
            </v-tooltip>
          </v-layout>
          <v-progress-circular v-else indeterminate="indeterminate" color="primary" class="py-2"></v-progress-circular>
        </v-flex>
        <v-flex xs12 :class="flex_settings['Operation'][lang]">
          <div v-if="hydrated && cabinet.dashboard_view && cabinet.dashboard_view.display_outputs">
            <div v-if="cabinet.class_name !== 'virtual_cabinet' && !devices_status[cabinet.id]">
              <v-progress-circular indeterminate="indeterminate" color="primary"></v-progress-circular>
            </div>
            <div
              v-else-if="cabinet.class_name === 'virtual_cabinet' || (devices_status[cabinet.id] && cabinet.linked_outputs.length === 0)"
              class="subheading py-2"
            >
              {{ $t('Not Available') }}
            </div>
            <v-layout v-else row wrap class="py-2">
              <template v-if="cabinet.display_new_connections_screen">
                <v-tooltip top style="z-index: 5" v-for="output in cabinet.linked_outputs" :key="`output-${cabinet.id}-${output.gpio_address}`">
                  <v-flex
                    slot="activator"
                    :class="lang === 'en' ? 'mr-1 mt-1' : 'ml-1 mt-1'"
                    d-flex
                    shrink
                    justify-center
                    align-center
                    :style="{
                      'border-radius': '6px',
                      'background-color': !cabinet.dashboard_view.enable_outputs ? disabled_color : getOutputStatusNewFormat(output, outputs_status[cabinet.id][output.gpio_address]) === true ? '#FBC02D' : '#B0BEC5',
                      'height': '25px',
                      'width': '25px'
                    }"
                  >
                    <span class="font-weight-medium body-2 white--text">{{ output.gpio_address }}</span>
                  </v-flex>
                  <span>{{ `${$t(output.clock_type)} ${$t('On')}: ${output.on_time} ${$t('Off')}: ${output.off_time}` }}</span>
                </v-tooltip>
              </template>
              <template v-else>
                <v-tooltip top style="z-index: 5" :key="`output-${cabinet.id}-${output.output_num}`" v-for="output in cabinet.linked_outputs">
                  <v-flex
                    slot="activator"
                    :class="lang === 'en' ? 'mr-1 mt-1' : 'ml-1 mt-1'"
                    d-flex
                    shrink
                    justify-center
                    align-center
                    :style="{
                      'border-radius': '6px',
                      'background-color': !cabinet.dashboard_view.enable_outputs ? disabled_color : getOutputStatus(output.configuration, outputs_status[cabinet.id][output.output_num]) === true ? '#FBC02D' : '#B0BEC5',
                      'height': '25px',
                      'width': '25px'
                    }"
                  >
                    <span class="font-weight-medium body-2 white--text">{{ output.output_num }}</span>
                  </v-flex>
                  <span>{{ `${$t(getTitleOperation(cabinet, output.output_num))} ${$t('On')}: ${output.on_time} ${$t('Off')}: ${output.off_time}` }}</span>
                </v-tooltip>
              </template>
            </v-layout>
          </div>
        </v-flex>
        <v-flex xs12 :class="flex_settings['Monitor'][lang]">
          <div v-if="hydrated && cabinet.dashboard_view && cabinet.dashboard_view.display_inputs">
            <div v-if="cabinet.class_name !== 'virtual_cabinet' && !devices_status[cabinet.id]">
              <v-progress-circular indeterminate="indeterminate" color="primary"></v-progress-circular>
            </div>
            <div
              v-else-if="cabinet.class_name === 'virtual_cabinet' || (devices_status[cabinet.id] && cabinet.linked_inputs.length === 0)"
              class="subheading py-2"
            >
              {{ $t('Not Available') }}
            </div>
            <v-layout row wrap v-else class="py-2">
              <template v-if="cabinet.display_new_connections_screen">
                <v-tooltip
                  top
                  style="z-index: 5"
                  :key="`input-${cabinet.id}-${input.gpio_address}`"
                  v-for="input in cabinet.linked_inputs"
                >
                  <v-flex
                    slot="activator"
                    :class="lang === 'en' ? 'mr-1 mt-1' : 'ml-1 mt-1'"
                    d-flex
                    shrink
                    justify-center
                    align-center
                    :style="{
                      'border-radius': '6px',
                      'background-color': !cabinet.dashboard_view.enable_inputs ? disabled_color : getInputColorNewFormat(inputs_status[cabinet.id][input.gpio_address]),
                      'height': '25px',
                      'width': '25px'
                    }"
                  >
                    <span class="font-weight-medium body-2 white--text">{{ input.gpio_address }}</span>
                  </v-flex>
                  <span>{{ `${$t(input.clock_type)} ${$t('Status')}: ${inputs_status[cabinet.id][input.gpio_address] === true ? $t('On') : $t('Off')}` }}</span>
                </v-tooltip>
              </template>
              <template v-else>
                <v-tooltip
                  top
                  style="z-index: 5"
                  :key="`input-${cabinet.id}-${input.input_num}`"
                  v-for="input in cabinet.linked_inputs"
                >
                  <v-flex
                    slot="activator"
                    :class="lang === 'en' ? 'mr-1 mt-1' : 'ml-1 mt-1'"
                    d-flex
                    shrink
                    justify-center
                    align-center
                    :style="{
                      'border-radius': '6px',
                      'background-color': !cabinet.dashboard_view.enable_inputs ? disabled_color : getInputColor(input.configuration, inputs_status[cabinet.id][input.input_num]),
                      'height': '25px',
                      'width': '25px'
                    }"
                  >
                    <span class="font-weight-medium body-2 white--text">{{ input.input_num }}</span>
                  </v-flex>
                  <span>{{ `${$t('Status')}: ${inputs_status[cabinet.id][input.input_num] === true ? $t('On') : $t('Off')}` }}</span>
                </v-tooltip>
              </template>
            </v-layout>
          </div>
        </v-flex>
        <v-flex xs12 :class="flex_settings['Operation Window'][lang]">
          <div class="py-2 d-flex align-center" v-if="hydrated">
            <v-img v-if="cabinet.operation_window === '24/7'" :class="lang === 'en' ? 'mr-2' : 'ml-2'" contain :src="allDayIcon()" height="20" width="20"></v-img>
            <v-img v-else :class="lang === 'en' ? 'mr-2' : 'ml-2'" contain :src="partialDayIcon()" height="20" width="20"></v-img>
            <span class="subheading">{{ cabinet.operation_window }}</span>
          </div>
        </v-flex>
      </v-layout>
    </v-layout>
    <v-divider light></v-divider>
  </div>
</template>

<script>
import { getMapIcon } from '@/pages/mapView/components/map/getMapIcon';
import VIRTUAL_CABINET_ICON from '@/assets/images/mapView_icons/virtual_cabinet/virtual_cabinet.svg';
import CABINET_CONNECTED_ICON from '@/assets/images/cabinet_details_icons/cabinet_connected.svg';
import CABINET_DISCONNECTED_ICON from '@/assets/images/cabinet_details_icons/cabinet_disconnected.svg';
import CABINET_FAULT_ICON from '@/assets/images/mapView_icons/cabinet/cabinet_fault.svg';
import CABINET_IN_INSTALLATION_ICON from '@/assets/images/mapView_icons/cabinet/cabinet_in_installation.svg';
import CABINET_RETROFIT_ICON from '@/assets/images/mapView_icons/cabinet/cabinet_retrofit.svg';
import CABINET_PENDING_VERIFICATION_ICON from '@/assets/images/mapView_icons/cabinet/cabinet_pending_verification.svg';
import ALL_DAY_ICON from '@/assets/images/cabinet_details_icons/all_day_icon.png';
import PARTIAL_DAY_ICON from '@/assets/images/cabinet_details_icons/partial_day_icon.png';
import BELL_ICON from '@/assets/images/cabinet_details_icons/bell.svg';
import GREEN_BELL_ICON from '@/assets/images/cabinet_details_icons/green_bell.svg';
import CALENDAR_ICON from '@/assets/images/cabinet_details_icons/calendar.svg';
import GREEN_CALENDAR_ICON from '@/assets/images/cabinet_details_icons/green_calendar.svg';
import STREET_LIGHT_ICON from '@/assets/images/cabinet_details_icons/street_light.svg';
import GREEN_STREET_LIGHT_ICON from '@/assets/images/cabinet_details_icons/green_street_light.svg';
import CABINET_ALERTS_ICON from '@/assets/images/cabinet_details_icons/cabinet_alerts.svg';
import GREEN_CABINET_ALERTS_ICON from '@/assets/images/cabinet_details_icons/green_cabinet_alerts.svg';

export default {
  name: 'CabinetRow',
  props: {
    // vue-virtual-scroll-list passes the row item as `source`
    source: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      default: 0
    },
    // extra-props from the parent
    managed_area: {
      type: String,
      default: ''
    },
    lang: {
      type: String,
      default: 'en'
    },
    headers: {
      type: Array,
      default: () => []
    },
    flex_settings: {
      type: Object,
      default: () => ({})
    },
    devices_status: {
      type: Object,
      default: () => ({})
    },
    inputs_status: {
      type: Object,
      default: () => ({})
    },
    outputs_status: {
      type: Object,
      default: () => ({})
    },
    fixtures_by_cabinet: {
      type: Object,
      default: () => ({})
    },
    disabled_color: {
      type: String,
      default: 'rgba(0,0,0,0.12)'
    },
    onCabinetClick: {
      type: Function,
      default: null
    }
  },
  computed: {
    cabinet() {
      return this.source;
    },
    hydrated() {
      return !!this.devices_status[this.cabinet.id];
    },
    icon_size() {
      return this.cabinet.class_name === 'virtual_cabinet' || this.cabinet.sys___active === 0 ? 30 : 35;
    },
    fixtures_summary() {
      return this.fixtures_by_cabinet[this.cabinet.id]
        || { total: 0, connected_active: 0, retrofit_count: 0, percentage: 0, loaded: false };
    },
    retrofitTooltipText() {
      return `${this.fixtures_summary.retrofit_count} ${this.$t('devices in Retrofit state are excluded from this percentage')}`;
    }
  },
  methods: {
    onClick() {
      if (typeof this.onCabinetClick === 'function') {
        this.onCabinetClick({ cabinet: this.cabinet, managed_area: this.managed_area });
      }
    },
    getCabinetStatusIcon(device) {
      // Retrofit / Pending Verification / In Installation override everything
      // else, exactly like on the map. Everything else keeps this page's own
      // connected / disconnected icons - the map's on/off icons describe the
      // lighting state, which this table doesn't show.
      // Slim cabinet rows come from their own fetch and carry no __iconKey, so
      // getMapIcon resolves it from cabinet.operation_state / sys fields.
      const icon_key = device.__iconKey || getMapIcon(device, false);
      const OVERRIDE_ICONS_BY_KEY = {
        cabinetRetrofit: CABINET_RETROFIT_ICON,
        cabinetPendingVerification: CABINET_PENDING_VERIFICATION_ICON,
        cabinetInInstallation: CABINET_IN_INSTALLATION_ICON
      };
      if (OVERRIDE_ICONS_BY_KEY[icon_key]) return OVERRIDE_ICONS_BY_KEY[icon_key];

      if (device.class_name === 'virtual_cabinet') return VIRTUAL_CABINET_ICON;
      if (device.sys___active === 0) return CABINET_FAULT_ICON;
      if (device.sys___connected === 1) return CABINET_CONNECTED_ICON;
      if (device.sys___connected === 0) return CABINET_DISCONNECTED_ICON;
      return CABINET_FAULT_ICON;
    },
    bellIcon(green) { return green ? GREEN_BELL_ICON : BELL_ICON; },
    calendarIcon(green) { return green ? GREEN_CALENDAR_ICON : CALENDAR_ICON; },
    streetLightIcon(green) { return green ? GREEN_STREET_LIGHT_ICON : STREET_LIGHT_ICON; },
    cabinetAlertsIcon(green) { return green ? GREEN_CABINET_ALERTS_ICON : CABINET_ALERTS_ICON; },
    virtualCabinetIcon() { return VIRTUAL_CABINET_ICON; },
    allDayIcon() { return ALL_DAY_ICON; },
    partialDayIcon() { return PARTIAL_DAY_ICON; },
    checkInputIsOpposite(config) {
      return config.toLowerCase().includes('1=tripped') || config.toLowerCase().includes('1=off');
    },
    checkOutputIsOpposite(config) {
      return config.toLowerCase().includes('1=off');
    },
    getInputColor(input_config, status) {
      const config_is_opposite = this.checkInputIsOpposite(input_config);
      const input_is_on = config_is_opposite ? !status : status;
      return input_is_on ? '#7FB77E' : '#E97777';
    },
    getInputColorNewFormat(status) {
      return status ? '#7FB77E' : '#E97777';
    },
    getOutputStatus(output_config, status) {
      if (typeof status !== 'boolean') return status;
      const config_is_opposite = this.checkOutputIsOpposite(output_config);
      return config_is_opposite ? !status : status;
    },
    getOutputStatusNewFormat(output_config, status) {
      if (typeof status !== 'boolean') return status;
      return status;
    },
    getTitleOperation(device, output) {
      const schedule = device.schedule[`${output}`] || null;
      if (schedule && schedule['is_astronomical'] === true) return 'Astral';
      if (schedule && schedule['is_astronomical'] === false) return 'Clock';
      return 'N/A';
    }
  }
};
</script>
